import { type ReactElement, type ReactNode, useState } from 'react';

import { shiftNoteItemLabel } from '@apps/web/components/shift_notes/shift_note_presentation';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useShiftNotes } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type Shift,
    type ShiftNoteRead,
} from '@libraries/typescript/api_client';

export function ShiftNoteTooltip({
    shift,
    summary,
    children,
}: {
    readonly shift: Shift;
    readonly summary: ReactNode;
    readonly children: ReactElement;
}) {
    const [open, setOpen] = useState(false);

    return (
        <Tooltip open={open} onOpenChange={setOpen}>
            <TooltipTrigger asChild={true}>{children}</TooltipTrigger>
            <TooltipContent className="max-h-80 max-w-sm overflow-y-auto text-left text-pretty">
                <div>{summary}</div>
                {open ? <ShiftNotePreview shift={shift} /> : null}
            </TooltipContent>
        </Tooltip>
    );
}

function ShiftNotePreview({ shift }: { readonly shift: Shift }) {
    const { data, error, isLoading } = useShiftNotes(
        shift.organization_id,
        [shift.id],
        {
            enabled: true,
            reason: 'shift-calendar-tooltip',
        },
    );
    const notes: ReadonlyArray<ShiftNoteRead> =
        data?.notes_by_shift_id[shift.id] ?? [];

    if (isLoading && !data) {
        return (
            <p className="border-primary-foreground/20 mt-2 border-t pt-2">
                Loading memo or notes…
            </p>
        );
    }
    if (error) {
        return (
            <p className="border-primary-foreground/20 mt-2 border-t pt-2">
                Couldn’t load memo or notes.
            </p>
        );
    }
    if (notes.length === 0) {
        return null;
    }

    return (
        <div className="border-primary-foreground/20 mt-2 space-y-2 border-t pt-2">
            {notes.map((note) => (
                <div key={note.id} className="space-y-0.5">
                    <p className="font-semibold">{shiftNoteItemLabel(note)}</p>
                    <p className="break-words whitespace-pre-wrap">
                        {note.body}
                    </p>
                </div>
            ))}
        </div>
    );
}
